import { Injectable } from '@angular/core';
import { distinctUntilChanged, map, Observable, shareReplay, startWith, combineLatest, of } from 'rxjs';
import { actionId, actionType } from 'src/mqtt/actions-list';
import { IShadowState } from 'src/mqtt/interfaces/interfaces';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { getActionKey } from 'src/mqtt/utils/device-name-parser';
import { IHVACDeviceBase, ScheduleDayType } from '../interfaces/climate.interface';
import { DeviceRegistryService } from 'src/app/services/device-registry.service';

@Injectable({
  providedIn: 'root'
})
export class ClimateControlService {

  private _hvacUnits$!: Observable<string[]>;

  constructor(
    private mqtt: MqttConnectorService,
    private deviceRegistry: DeviceRegistryService,
  ) {
    // Use device registry as the primary source - no more group.climate dependency
    this._hvacUnits$ = this.deviceRegistry.getRegistry().pipe(
      map((registry) => {
        // Get expected devices from registry (includes both discovered and pending)
        const devices = this.deviceRegistry.getExpectedDevices('climate');
        return devices;
      }),
      // Only emit when the array actually changes
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public get hvacUnits() {
    return this._hvacUnits$;
  }

  public getDeviceBaseData(deviceId: string, deviceState: IShadowState): IHVACDeviceBase | null {
    const attributes = deviceState?.reported?.attributes;
    if (!attributes) return null;

    // Check if the device is actually unavailable
    const isDeviceUnavailable = deviceState?.reported.state === 'unavailable';
    
    const device: IHVACDeviceBase = {
      deviceId,
      deviceName: attributes['friendly_name'],
      // For unavailable devices, set currentTemp to undefined to indicate unknown
      currentTemp: isDeviceUnavailable ? undefined : this.defineNumber(attributes['current_temperature']),
      // If state is 'unavailable', don't use it directly as currentMode
      // Instead, use a descriptive value that indicates the device is not responding
      currentMode: isDeviceUnavailable ? 'unavailable' : (deviceState?.reported.state || 'off'),
      // Mark as unavailable if no temperature or state is 'unavailable'
      isUnavailable: !attributes['current_temperature'] || isDeviceUnavailable,
      hvacAction: attributes['hvac_action'],
    }

    // Handle temperature setting differently for unavailable devices
    if (isDeviceUnavailable) {
      // For unavailable devices, mark the target temp as undefined to indicate unknown
      device.setToTemp = undefined;
    } else if (attributes && (attributes['target_temp_low'] || attributes['temperature'])) {
      // Use the temperature attribute if available
      device.setToTemp = this.defineNumber(attributes['target_temp_low'] || attributes['temperature']);
    } else if (device.currentMode !== 'off' && device.currentTemp) {
      // If device is on but no target temp, use current temp as a reasonable default
      device.setToTemp = device.currentTemp;
    } else {
      // Device is off or no temperature info available
      device.setToTemp = device.currentMode === 'off' ? 0 : 72; // Default to 72°F for devices that are on but have no temp info
    }

    return device;
  }

  public setTemperature(deviceId: string, temperature: number): Observable<void> {
    return this.mqtt.setAttributeValue(
      `${actionType.climate}.${deviceId}`,
      {
        temperature: temperature,
        'target_temp_low': temperature,
        'target_temp_high': temperature,
      }
    )
  }

  public setMode(deviceId: string, mode: string): Observable<void> {
    // Don't allow setting mode if mode is 'unavailable'
    if (mode === 'unavailable') {
      console.warn('Cannot set climate mode to "unavailable" - this is a status, not a command');
      return of(undefined);
    }
    
    return this.mqtt.setStateValue(
      `${actionType.climate}.${deviceId}`,
      mode.toLowerCase()
    )
  }

  public setEcoMode(deviceId: string, enable: boolean): Observable<void> {
    return this.mqtt.setInputBooleanValue(
      `${actionType.inputBoolean}.${deviceId}${actionId.ecoMode}`,
      enable
    )
  }

  public setWakeTemp(deviceId: string, temperature: number, day: ScheduleDayType = 'weekday'): Observable<void> {
    const suffix = day === 'weekend' ? actionId.weekendWakeTemp : actionId.wakeTemp;
    return this.mqtt.setStateValue(
      `${actionType.inputNumber}.${deviceId}${suffix}`,
      temperature
    )
  }

  public setWakeTime(deviceId: string, time: string, day: ScheduleDayType = 'weekday'): Observable<void> {
    const suffix = day === 'weekend' ? actionId.weekendWakeTime : actionId.wakeTime;
    return this.mqtt.setStateValue(
      `${actionType.inputDateTime}.${deviceId}${suffix}`,
      time
    )
  }

  public setWakeMode(deviceId: string, mode: string, day: ScheduleDayType = 'weekday'): Observable<void> {
    const suffix = day === 'weekend' ? actionId.weekendWakeMode : actionId.wakeMode;
    return this.mqtt.setInputTextValue(
      `${actionType.inputText}.${deviceId}${suffix}`,
      mode
    )
  }

  public setSleepTemp(deviceId: string, temperature: number, day: ScheduleDayType = 'weekday'): Observable<void> {
    const suffix = day === 'weekend' ? actionId.weekendSleepTemp : actionId.sleepTemp;
    return this.mqtt.setStateValue(
      `${actionType.inputNumber}.${deviceId}${suffix}`,
      temperature
    )
  }

  public setSleepTime(deviceId: string, time: string, day: ScheduleDayType = 'weekday'): Observable<void> {
    const suffix = day === 'weekend' ? actionId.weekendSleepTime : actionId.sleepTime;
    return this.mqtt.setStateValue(
      `${actionType.inputDateTime}.${deviceId}${suffix}`,
      time
    )
  }

  public setSleepMode(deviceId: string, mode: string, day: ScheduleDayType = 'weekday'): Observable<void> {
    const suffix = day === 'weekend' ? actionId.weekendSleepMode : actionId.sleepMode;
    return this.mqtt.setInputTextValue(
      `${actionType.inputText}.${deviceId}${suffix}`,
      mode
    )
  }

  public setAutoSet(deviceId: string, enable: boolean): Observable<void> {
    return this.mqtt.setInputBooleanValue(
      `${actionType.inputBoolean}.${deviceId}${actionId.autoSet}`,
      enable
    )
  }

  public setScheduleSplit(deviceId: string, enable: boolean): Observable<void> {
    return this.mqtt.setInputBooleanValue(
      `${actionType.inputBoolean}.${deviceId}${actionId.scheduleSplit}`,
      enable
    )
  }

  private defineNumber(value: string | number | undefined | null): number | undefined {
    if (!value) return undefined;
    if (typeof value === 'number') return value;

    if (value.includes('.')) {
      return +(value.substring(0, value.indexOf('.')));
    } else {
      return +value;
    }
  }
}
